import React from "react";
import PropTypes from "prop-types";
import { Card, CardContent, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import { formatCompactNumber, formatSignedCompact, trendTone } from "./dashboardFormatters";
import { HEADER_COLOR, POPPINS } from "./dashboardTypography";

const toneColor = {
  positive: "success.main",
  negative: "error.main",
  neutral: "text.secondary",
};

const TrendIcon = ({ tone }) => {
  if (tone === "positive") return <TrendingUpIcon sx={{ fontSize: 16 }} />;
  if (tone === "negative") return <TrendingDownIcon sx={{ fontSize: 16 }} />;
  return <HorizontalRuleIcon sx={{ fontSize: 16 }} />;
};

TrendIcon.propTypes = {
  tone: PropTypes.oneOf(["positive", "negative", "neutral"]).isRequired,
};

const KpiTile = ({ label, value, exactValue, comparison, loading }) => {
  const tone = trendTone(comparison?.pctChange);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        boxShadow: "0 10px 24px rgba(16, 24, 40, 0.06)",
        minHeight: 132,
      }}
    >
      <CardContent>
        {loading ? (
          <>
            <Skeleton width="50%" />
            <Skeleton width="70%" height={42} />
            <Skeleton width="80%" />
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ ...POPPINS, fontSize: "0.78rem", fontWeight: 500, mb: 0.5 }}>
              {label}
            </Typography>
            <Tooltip title={exactValue ?? "No Data"}>
              <Typography variant="h5" sx={{ ...POPPINS, fontWeight: 700, letterSpacing: -0.2, color: HEADER_COLOR }}>
                {value === null || value === undefined ? "N/A" : formatCompactNumber(value)}
              </Typography>
            </Tooltip>
            {comparison ? (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
                <Typography sx={{ ...POPPINS, color: toneColor[tone], display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: "0.74rem", fontWeight: 600 }}>
                  <TrendIcon tone={tone} />
                  {formatSignedCompact(comparison?.pctChange, true)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ...POPPINS, fontSize: "0.72rem" }}>
                  vs prev, {formatSignedCompact(comparison?.yoyChange, true)} YoY
                </Typography>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ ...POPPINS, fontSize: "0.72rem", mt: 1, display: "block" }}>
                Comparison unavailable
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

KpiTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  exactValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  comparison: PropTypes.shape({
    pctChange: PropTypes.number,
    yoyChange: PropTypes.number,
  }),
  loading: PropTypes.bool,
};

KpiTile.defaultProps = {
  value: null,
  exactValue: null,
  comparison: null,
  loading: false,
};

export default KpiTile;
